import Dialog from "@mui/material/Dialog";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Alert, CircularProgress, TextField } from "@mui/material";
import React, { useMemo, useState } from "react";
import "./WorktimePage.scss";
import { useAppDispatch } from "../../redux-store";
import {
  startTrip as startTripActivity,
  endTrip as endTripActivity,
} from "../../ducks/app/driver-activities";
import { useParams } from "react-router";
import debounce from "lodash/debounce";
import { loadRouteWithStops } from "../../ducks/data/route-with-stops";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useTranslation } from "react-i18next";
import { RouteStartedEndedData } from "../../ducks/data/route-with-stops/types";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { drivingUrl, parseTrailerIdParam, routesLoginUrl } from "../../routes";
import { DateInput, TimeInput } from "../../components/TimeInput";
import { isNetworkError } from "../../helpers/networkError";

const StartEndDayDialog = ({
  onClose,
  data,
  minDate,
  type,
}: {
  onClose: () => void;
  data: RouteStartedEndedData | null;
  minDate?: string;
  type: "START" | "END";
}) => {
  const { t } = useTranslation(["translation", "components"]);
  const params = useParams();
  const routeId = params.routeId as string;
  const [date, setDate] = useState<DateTime | null>(
    data?.dateTime ? DateTime.fromISO(data.dateTime) : null
  );
  const [note, setNote] = useState<string>(data?.note || "");
  const [odometer, setOdometer] = useState<string>(data?.odometer || "");
  const [loading, setLoading] = useState(false);
  const [kmError, setKmError] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<
    "networkError" | "technicalError" | null
  >(null);
  const truckId = params.truckId as string;
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);
  console.log("truckId", truckId);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onConfirmPress = useMemo(
    () =>
      debounce(
        () => {
          setLoading(true);
          setSubmitErrorKey(null);

          // Choose action based on feature flag and type
          let actionPromise;
          if (type === "START") {
            actionPromise = dispatch(
              startTripActivity({
                routeId,
                odometer: Math.round(+odometer),
                note,
                dateTime: date,
                truckId: truckId!,
                unpoweredTrailerId,
              })
            );
          } else if (type === "END") {
            actionPromise = dispatch(
              endTripActivity({
                routeId,
                odometer: Math.round(+odometer),
                note,
                dateTime: date,
                trailerId: truckId!,
                unpoweredTrailerId,
              })
            );
          } else {
            throw new Error(`Invalid type: ${type}`);
          }

          actionPromise
            .unwrap()
            .then(async () => {
              await new Promise((r) => setTimeout(r, 500));
              dispatch(loadRouteWithStops(routeId));
              onClose();
              if (data === null && type === "START") {
                navigate(
                  drivingUrl({
                    routeId: routeId,
                    truckId: truckId!,
                    trailerId: unpoweredTrailerId,
                  })
                );
              }
              if (type === "END") {
                navigate(routesLoginUrl);
              }
            })
            .catch((e) => {
              setSubmitErrorKey(
                isNetworkError(e) ? "networkError" : "technicalError"
              );
            })
            .finally(() => {
              setLoading(false);
            });
        },
        1000,
        { leading: true, trailing: false }
      ),
    [
      setLoading,
      odometer,
      note,
      dispatch,
      onClose,
      type,
      routeId,
      date,
      truckId,
      unpoweredTrailerId,
    ]
  );

  const isPrimaryButtonDisabled = useMemo(() => {
    if (loading || kmError) {
      return true;
    }
    return !!(
      minDate &&
      date &&
      DateTime.fromISO(minDate).toMillis() > date.toMillis()
    );
  }, [loading, minDate, date, kmError]);

  const onKmChange = (value: string) => {
    setOdometer(value);
    if (Number(value) > 2000000000 || Number(value) < 0) {
      setKmError(true);
    } else {
      setKmError(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <div
        className="flex flex-col gap-4"
        style={{ padding: "32px 16px", minWidth: 300 }}
      >
        {date && (
          <>
            <div style={{ fontSize: 18, fontWeight: 500 }}>
              {t("translation:startStopDialog.dialogHeading2")}
            </div>
            <DateInput
              value={date.toISODate()}
              onChange={(e) => {
                if (!e.target.value) return;
                const eventDate = DateTime.fromISO(e.target.value);
                if (!eventDate.isValid) return;
                setDate(
                  date!.set({
                    day: eventDate.day,
                    month: eventDate.month,
                    year: eventDate.year,
                  })
                );
              }}
              label={"Date"}
            />
            <TimeInput
              value={date.toFormat("HH:mm")}
              onChange={(e) => {
                if (!e.target.value) return;
                const time = DateTime.fromFormat(e.target.value, "HH:mm");
                if (!time.isValid) return;
                setDate(
                  date!.set({
                    hour: time.get("hour"),
                    minute: time.get("minute"),
                  })
                );
              }}
            />
          </>
        )}
        <div style={{ fontSize: 18, fontWeight: 500 }}>
          {t("translation:startStopDialog.dialogHeading")}
        </div>

        <div style={{ fontSize: 14, fontWeight: 400, color: "#666666" }}>
          {type === "START"
            ? t("translation:startStopDialog.dialogContent_START")
            : t("translation:startStopDialog.dialogContent_STOP")}
        </div>

        <TextField
          {...(!date && { autoFocus: true })}
          fullWidth
          type="number"
          error={kmError}
          label={"Km"}
          value={odometer}
          onChange={(e) => onKmChange(e.target.value)}
        />
        {kmError && (
          <div className="mt-1 mb-2 text-red-500">
            {t("translation:startStopDialog.kmError")}
          </div>
        )}
        <TextField
          fullWidth
          type="text"
          label={"Note"}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
        {submitErrorKey && (
          <Alert severity="error">
            {submitErrorKey === "networkError"
              ? t("translation:networkError")
              : t("translation:technicalError")}
          </Alert>
        )}
        <div>
          <button
            className={
              type === "START"
                ? "time-tracking-button green"
                : "time-tracking-button red"
            }
            disabled={isPrimaryButtonDisabled}
            onClick={() => !kmError && onConfirmPress()}
          >
            {loading ? (
              <CircularProgress size={22} style={{ color: "white" }} />
            ) : type === "START" ? (
              <PlayCircleOutlineIcon />
            ) : (
              <StopCircleIcon />
            )}
            <div>
              {loading
                ? t("components:buttonLabels.gettingGps")
                : date
                  ? t("translation:submitButtonText")
                  : type === "START"
                    ? t("translation:startStopDialog.submitButtonText_START")
                    : t("translation:startStopDialog.submitButtonText_STOP")}
            </div>
          </button>
          <button className="time-tracking-button hollow" onClick={onClose}>
            {t("translation:startStopDialog.cancelButtonText")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default StartEndDayDialog;
